import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { ColumnsPanelTrigger, FilterPanelTrigger, Toolbar, useGridApiContext } from '@mui/x-data-grid-pro';
import { debounce } from 'essential-utils';
import { observe } from 'mobx';
import React, { useEffect, useState } from 'react';
import { Autocomplete } from '../../autocomplete';
import { Badge } from '../../badge';
import { FlexBox } from '../../box';
import { Button } from '../../button';
import { IconFilterList, IconReplay, IconSearch, IconTableRows, IconViewColumn } from '../../icon';
import { Menu } from '../../menu';
import { styled } from '../../styled';
import { TextField } from '../../textfield';
import { Typography } from '../../typography';
import { useFirstLoad } from './use-first-load';
const StyledToolbar = styled(Toolbar)(({ theme }) => ({
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(2),
    minHeight: 'auto',
    borderBottom: 'none'
}));
export const DataGridToolbar = (props) => {
    const apiRef = useGridApiContext();
    const { localeText, resetFilterComponent: ResetFilterComponent, sortToolbarConfig: _sortToolbarConfig, sortModel, filterModel, gridConfig, columns, hideQuickFilter = false, hideToolbarFilter = false, hideToolbarColumns = false, loading = false, enableDensity = false, onResetGridConfig } = props;
    const quickFilterValues = filterModel?.quickFilterValues?.[0] ?? '';
    const [searchValue, setSearchValue] = React.useState(quickFilterValues);
    const [gridConfigChanged, setGridConfigChanged] = useState(false);
    const { currentSortModel, sortToolbarConfig } = getSortConfigs(columns, sortModel, _sortToolbarConfig);
    const setQuickFilterValues = React.useMemo(() => debounce((value) => {
        apiRef.current.setQuickFilterValues([value]);
    }, 300), [apiRef]);
    useEffect(() => {
        if (gridConfig) {
            return observe(gridConfig, 'configChanged', (change) => {
                setGridConfigChanged(change.newValue);
            });
        }
        return;
    }, [gridConfig]);
    useEffect(() => {
        setSearchValue(quickFilterValues);
    }, [quickFilterValues]);
    const [initialSortModel] = useState(sortModel);
    const [initialFilterModel] = useState(filterModel);
    const firstLoad = useFirstLoad(loading);
    const onSearchChange = (value) => {
        setSearchValue(value);
        setQuickFilterValues(value);
    };
    const onResetFilterAndSorting = () => {
        setSearchValue('');
        props.onSortModelChange(initialSortModel ?? []);
        props.onFilterModelChange(initialFilterModel ?? { items: [] });
    };
    const DENSITY_OPTIONS = [
        {
            label: `${localeText?.toolbarDensityStandard}`,
            value: 'standard'
        },
        {
            label: `${localeText?.toolbarDensityComfortable}`,
            value: 'comfortable'
        }
    ];
    return (_jsxs(StyledToolbar, { "data-role": "toolbar", children: [_jsxs(FlexBox, { gap: 0.25, "data-role": "toolbar-actions", children: [!hideToolbarFilter && (_jsx(FilterPanelTrigger, { render: (_buttonProps, state) => {
                            const { ref: _ref, ...buttonProps } = _buttonProps;
                            return (_jsx(Button, { ref: _ref, ...buttonProps, "data-role": "toolbar-filter", children: _jsxs(FlexBox, { gap: 1, vAlign: "center", children: [state.filterCount > 0 ? (_jsx(Badge, { color: "primary", variant: "dot", children: _jsx(IconFilterList, { fontSize: "small", color: "primary" }) })) : (_jsx(IconFilterList, { fontSize: "small", color: "primary" })), _jsx(Typography, { variant: "subtitle2", children: localeText?.toolbarFilters })] }) }));
                        } })), !hideToolbarColumns && (_jsx(ColumnsPanelTrigger, { render: _jsx(Button, { "data-role": "toolbar-columns" }), children: _jsxs(FlexBox, { gap: 1, vAlign: "center", children: [_jsx(IconViewColumn, { fontSize: "small", color: "primary" }), _jsx(Typography, { variant: "subtitle2", children: localeText?.toolbarColumns })] }) })), enableDensity && (_jsx(Menu, { actions: DENSITY_OPTIONS.map((option) => ({
                            type: 'MenuItem',
                            primary: option.label,
                            item: option.value,
                            onClick: props.onDensityChange
                        })), children: _jsx(Button, { "data-role": "toolbar-density", variant: "text", children: _jsxs(FlexBox, { gap: 1, vAlign: "center", children: [_jsx(IconTableRows, { fontSize: "small", color: "primary" }), _jsx(Typography, { variant: "subtitle2", children: localeText?.toolbarDensityLabel })] }) }) }))] }), _jsxs(FlexBox, { flex: 1, justifyContent: "flex-end", children: [ResetFilterComponent && (_jsx(ResetFilterComponent, { onClick: onResetFilterAndSorting })), gridConfigChanged && (_jsx(Button, { variant: "text", "data-role": "toolbar-reset-config", startIcon: _jsx(IconReplay, { color: "text.primary" }), onClick: onResetGridConfig, color: "inherit", children: localeText?.columnsManagementReset }))] }), _jsxs(FlexBox, { gap: 1, children: [sortToolbarConfig && (_jsx(FlexBox, { width: 225, "data-role": "sort-toolbar", children: _jsx(Autocomplete, { disableSearch: true, disabled: firstLoad, label: localeText?.columnHeaderSortIconLabel, value: currentSortModel, options: sortToolbarConfig, getOptionKey: (option) => option.field, getOptionLabel: (option) => option.title ?? option.field, onChange: (sortModel) => {
                                if (!sortModel) {
                                    props.onSortModelChange(props.initialState?.sorting
                                        ?.sortModel ?? []);
                                    return;
                                }
                                if (sortModel.field === currentSortModel?.field) {
                                    return;
                                }
                                if (sortModel &&
                                    typeof sortModel.field === 'string') {
                                    props.onSortModelChange([
                                        {
                                            field: sortModel.field,
                                            sort: sortModel.sort
                                        }
                                    ]);
                                }
                                else {
                                    props.onSortModelChange([]);
                                }
                            } }) })), !hideQuickFilter && (_jsx(FlexBox, { "data-role": "toolbar-quick-filter", width: 200, children: _jsx(TextField, { clearable: true, placeholder: localeText?.toolbarQuickFilterPlaceholder, sx: { width: 200 }, disabled: firstLoad, value: searchValue, onChange: onSearchChange, bgColor: "background.paper", slotProps: {
                                input: {
                                    startAdornment: (_jsx(IconSearch, { variant: "rounded", color: "action.active", sx: { mr: 1 } }))
                                }
                            }, loading: searchValue ? firstLoad : false }) }))] })] }));
};
export function useToolbarSortingProps(columns, sortToolbarConfig) {
    return (sortToolbarConfig ?? []).reduce((acc, sort) => {
        const sortField = typeof sort.field === 'string' ? sort.field : undefined;
        if (sortField && !columns.some((col) => col.field === sortField)) {
            acc.toolbarColumns.push({
                field: sortField,
                headerName: sort.title,
                filterable: false,
                hideable: false,
                renderCell: ({ row }) => row[sortField]
            });
            acc.columnVisibilityModel[sortField] = false;
        }
        return acc;
    }, { toolbarColumns: [], columnVisibilityModel: {} });
}
function getSortConfigs(columns, sortModel, _sortToolbarConfig) {
    const [sortItem] = sortModel ?? [];
    let currentSortModel = _sortToolbarConfig?.find((item) => sortItem?.field === item.field);
    const sortToolbarConfig = _sortToolbarConfig
        ? [..._sortToolbarConfig]
        : undefined;
    if (sortToolbarConfig && !currentSortModel && sortItem) {
        const title = columns.find((col) => col.field === sortItem.field)?.headerName ??
            sortItem.field;
        currentSortModel = {
            ...sortItem,
            title
        };
        if (currentSortModel) {
            sortToolbarConfig.push(currentSortModel);
        }
    }
    return { currentSortModel, sortToolbarConfig };
}
